<template>
  <transition name="boot-banner-slide">
    <div v-if="visible" class="boot-error-banner" role="alert" aria-live="polite">
      <div class="boot-error-banner__inner">
        <q-icon name="warning" color="warning" size="24px" class="boot-error-banner__icon" />
        <div class="boot-error-banner__content">
          <div class="boot-error-banner__title">
            Some features may not work correctly
          </div>
          <div class="boot-error-banner__subtitle">
            The app hit an error during startup{{ bootSuffix }}. A hard reload usually fixes this.
          </div>
          <details v-if="latestError" class="boot-error-banner__details">
            <summary>Technical details</summary>
            <pre>{{ latestError.message }}
{{ latestError.stack || '' }}</pre>
          </details>
        </div>
        <div class="boot-error-banner__actions">
          <q-btn
            dense
            unelevated
            color="primary"
            :loading="reloading"
            label="Hard Reload"
            @click="onHardReload"
          />
          <q-btn
            dense
            flat
            label="Dismiss"
            :disable="reloading"
            @click="onDismiss"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  dismissBanner,
  getBootErrors,
  hardReloadClearingCaches,
  isBannerDismissed,
} from 'src/utils/bootSafety';

const visible = ref(false);
const reloading = ref(false);
const errors = ref(getBootErrors());

const latestError = computed(() => errors.value[0] ?? null);

const bootSuffix = computed(() => {
  const names: string[] = (window.__bootErrorNames as string[] | undefined) ?? [];
  if (names.length === 0) return '';
  return ` (${[...new Set(names)].join(', ')})`;
});

function refresh(): void {
  errors.value = getBootErrors();
  const hadThisSession = Boolean(window.__bootHadErrors);
  // Show only when this session had a boot error and the user hasn't dismissed recently.
  visible.value = hadThisSession && !isBannerDismissed();
}

async function onHardReload(): Promise<void> {
  reloading.value = true;
  try {
    await hardReloadClearingCaches();
  } catch (err) {
    console.error('[BootErrorBanner] hard reload failed:', err);
    reloading.value = false;
  }
}

function onDismiss(): void {
  dismissBanner();
  visible.value = false;
}

onMounted(() => {
  refresh();
});
</script>

<style scoped>
.boot-error-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9998;
  background: #fff8e1;
  border-bottom: 1px solid #f5c77e;
  color: #5c3b00;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.boot-error-banner__inner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 16px;
  max-width: 1400px;
  margin: 0 auto;
}

.boot-error-banner__icon {
  flex: 0 0 auto;
  margin-top: 2px;
}

.boot-error-banner__content {
  flex: 1 1 auto;
  min-width: 0;
}

.boot-error-banner__title {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
}

.boot-error-banner__subtitle {
  font-size: 13px;
  line-height: 1.4;
  margin-top: 2px;
  color: #6b4a00;
}

.boot-error-banner__details {
  margin-top: 6px;
  font-size: 12px;
}

.boot-error-banner__details summary {
  cursor: pointer;
  user-select: none;
  color: #6b4a00;
}

.boot-error-banner__details pre {
  margin: 6px 0 0;
  padding: 8px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 180px;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: #3d2800;
}

.boot-error-banner__actions {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  align-items: center;
}

.boot-banner-slide-enter-from,
.boot-banner-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.boot-banner-slide-enter-active,
.boot-banner-slide-leave-active {
  transition: transform 240ms ease, opacity 240ms ease;
}

@media (max-width: 600px) {
  .boot-error-banner__inner {
    flex-wrap: wrap;
  }

  .boot-error-banner__actions {
    width: 100%;
    justify-content: flex-end;
  }
}

/* Dark mode support */
body.body--dark .boot-error-banner {
  background: #3a2c00;
  border-bottom-color: #6b4a00;
  color: #ffe3a3;
}

body.body--dark .boot-error-banner__subtitle,
body.body--dark .boot-error-banner__details summary {
  color: #ffd580;
}

body.body--dark .boot-error-banner__details pre {
  background: rgba(255, 255, 255, 0.06);
  color: #ffe3a3;
}
</style>
